import {find, intersection, isEqual, map} from 'lodash';
import React, {Component} from 'react';
import {connect} from 'react-redux';

import {
  isAttachmentModel,
  UploadedAttachmentModel,
} from '../../../../../libs/shared/core/src/models/api/attachmentModel';
import {SendActionsEnum} from '../../../../../libs/shared/core/src/models/api/messageModel';
import {composerLocation} from '../../../../../libs/shared/core/src/models/tracking/contexts/composerTracking';
import {wrapInDataEventContext} from '../../../../../libs/shared/core/src/services/tracking/dataEventTrackingProvider';
import {
  EventEntryPointsEnum,
  InterfaceEventsEnum,
  trackInterfaceEvent,
} from '../../../../../libs/shared/core/src/services/tracking/tracking';
import {getSandboxWorkspaceFakeTeammateAlias} from '../../../../../libs/shared/core/src/store/data/teammates/commonTeammatesSelectors';
import {
  getHasAutoShareDrafts,
  getHasNewSynchronousP3,
} from '../../../../../libs/shared/core/src/store/featureFlags/featureFlagsSelectors';
import {ResolveStateProps} from '../../../../../libs/shared/core/src/store/helpers/commonReselectHelpers';
import {ResolveDispatchProps} from '../../../../../libs/shared/core/src/store/helpers/reduxHelpers';
import {CellsContext} from '../../../../../libs/shared/core/src/store/panels/cells/context/cellsContext';
import {ViewerPanelId} from '../../../../../libs/shared/core/src/store/panels/panelStateBase';
import {
  AppParametricSelector,
  createSelector,
  makeMapDispatchToProps,
  makeMapStateToProps,
} from '../../../../../srcCommon/helpers/reselectHelpers';
import {buildCommentUrlBuilder, buildDraftComment} from '../../../../helpers/models/commentHelpers';
import {AnnotationModel} from '../../../../models/api/annotationModel';
import {
  AnnotationEventTrackingSourcesEnum,
  AnnotationTypesEnum,
} from '../../../../models/api/annotationTypes';
import {CommentModel, CommentStatusesEnum} from '../../../../models/api/commentModel';
import {ConversationModel} from '../../../../models/api/conversationModel';
import {PermissionsEnum} from '../../../../models/api/roleModel';
import {AppState} from '../../../../store/appState';
import {getHasPermission} from '../../../../store/data/access/accessSelectors';
import {
  PublicSaveCommentOptions,
  saveCommentAsyncAction,
} from '../../../../store/data/comments/saveCommentActions';
import {sendCommentAsyncAction} from '../../../../store/data/comments/sendCommentAction';
import {
  getCompany,
  getHasAISummarizeFeature,
  getHasTrialExpired,
  getIsThirdPartyAIEnabledForCompany,
} from '../../../../store/data/company/companySelectors';
import {makeGetDraftComments} from '../../../../store/data/conversations/conversationsSelectors';
import {getAnnotations, getConversations} from '../../../../store/data/dataSelectors';
import {getPinnedSandboxWorkspaceInboxes} from '../../../../store/data/inboxes/inboxesSelectors';
import {getMentionableAliases} from '../../../../store/data/teammates/mentionTeammatesSelectors';
import {getUser, getUserId} from '../../../../store/data/teammates/teammatesSelectors';
import {getHasUserTeamTrialExpired} from '../../../../store/data/teams/teamsSelectors';
import {
  collapseCommentComposerAction,
  expandCommentComposerAction,
} from '../../../../store/metadata/commentComposer/commentComposerActions';
import {makeGetIsCommentComposerExpanded} from '../../../../store/metadata/commentComposer/commentComposerSelectors';
import {getFlatPanels} from '../../../../store/panels/panelsSelectors';
import {
  editLastCommentAction,
  viewerConversationCloseRequestedAction,
} from '../../../../store/panels/viewer/viewerPanelActions';
import {getEmojiValidationMap} from '../../../../store/stateless/emojiSelectors';
import {CommentComposer, SendCommentOptions, UpdateAnnotationOptions} from './commentComposer';

/*
 * Props.
 */

interface CommentComposerContainerPublicProps {
  panelId: ViewerPanelId;
  cellsContext: CellsContext;
  conversationId: number;
  shouldFocus?: boolean;
  setComposerHeight?: (arg: number) => void;
}

/*
 * Selectors.
 */

function getConversationId(_state: AppState, props: CommentComposerContainerPublicProps) {
  return props.conversationId;
}

type ConversationSelector = AppParametricSelector<
  CommentComposerContainerPublicProps,
  ConversationModel | undefined
>;
function makeGetConversation(): ConversationSelector {
  return createSelector(
    [getConversationId, getConversations],
    (conversationId, conversations) => conversations[conversationId],
  );
}

type CommentSelector = AppParametricSelector<CommentComposerContainerPublicProps, CommentModel | undefined>;
function makeGetComment(getConversation: ConversationSelector): CommentSelector {
  return createSelector([getUserId, makeGetDraftComments(getConversation)], (userId, draftComments) =>
    find(draftComments, (c) => c.authorId === userId && c.status === CommentStatusesEnum.DRAFT),
  );
}

function makeGetIsDisabled(getConversation: ConversationSelector) {
  return createSelector(
    [getHasPermission, getConversation, getHasTrialExpired, getHasUserTeamTrialExpired],
    (hasPermission, conversation, hasTrialExpired, hasUserTeamTrialExpired) =>
      !conversation ||
      !hasPermission(conversation.namespace, PermissionsEnum.CONVERSATION_COMMENT) ||
      hasTrialExpired ||
      hasUserTeamTrialExpired,
  );
}

function makeGetCommentUid(getComment: CommentSelector) {
  return createSelector([getComment], (comment) => comment && comment.uid);
}

function makeGetAnnotationForComment(getComment: CommentSelector) {
  return createSelector([getAnnotations, getComment], (annotations, comment) =>
    comment?.annotationUid ? annotations[comment?.annotationUid] : undefined,
  );
}

function makeGetConversationMentionableAliases(getConversation: ConversationSelector) {
  return createSelector(
    [
      getMentionableAliases,
      getSandboxWorkspaceFakeTeammateAlias,
      getPinnedSandboxWorkspaceInboxes,
      getConversation,
    ],
    (
      mentionableAliases,
      sandboxWorkspaceFakeTeammateAlias,
      sandboxWorkspaceInboxes,
      conversation,
    ): ReadonlySet<string> => {
      const sandboxWorkspaceInboxIds = map(sandboxWorkspaceInboxes, (inbox) => inbox.id);
      const isSandboxWorkspaceConversation =
        intersection(conversation?.inboxIds ?? [], sandboxWorkspaceInboxIds).length > 0;
      if (isSandboxWorkspaceConversation && sandboxWorkspaceFakeTeammateAlias) {
        return new Set(mentionableAliases).add(sandboxWorkspaceFakeTeammateAlias);
      }
      return mentionableAliases;
    },
  );
}

function makeGetHasPopupPanel() {
  return createSelector([getFlatPanels], (panels) => panels.some((panel) => panel.popupAppPanelId));
}

function makeGetIsAIDisabled() {
  return createSelector(
    [getHasAISummarizeFeature, getIsThirdPartyAIEnabledForCompany],
    (hasAISummarizeFeature, isThirdPartyAIEnabled) => !hasAISummarizeFeature || !isThirdPartyAIEnabled,
  );
}

/*
 * Component.
 */

type CommentComposerStateProps = ResolveStateProps<typeof mapStateToProps>;
type CommentComposerDispatchProps = ResolveDispatchProps<typeof mapDispatchToProps>;
type CommentComposerContainerProps = CommentComposerContainerPublicProps &
  CommentComposerStateProps &
  CommentComposerDispatchProps;

interface CommentComposerContainerState {
  comment?: CommentModel;
  attachments?: ReadonlyArray<UploadedAttachmentModel>;
  referencedActivityId?: number;
  annotation?: AnnotationModel;
  isExpanded: boolean;
}

class CommentComposerContainerComponent extends Component<
  CommentComposerContainerProps,
  CommentComposerContainerState
> {
  constructor(props: CommentComposerContainerProps) {
    super(props);

    this.state = {
      isExpanded: false,
    };
  }

  /*
   * Lifecycle.
   */

  componentDidMount() {
    this.updateStateComment(this.props.comment, this.props.annotation);
  }

  componentDidUpdate(previousProps: Readonly<CommentComposerContainerProps>) {
    const {conversation, comment, annotation} = this.props;
    const didCommentChange = previousProps.comment !== comment;
    const didConversationChange = previousProps.conversation?.id !== conversation?.id;
    const didAnnotationChange = previousProps.annotation !== this.props.annotation;

    if (!didCommentChange && !didConversationChange && !didAnnotationChange) {
      return;
    }

    this.updateStateComment(comment, annotation);
  }

  private updateStateComment(
    comment: CommentModel | undefined,
    newAnnotation: AnnotationModel | undefined,
    onFinish?: () => void,
  ) {
    const newComment = comment || this.makeDraftComment();

    if (newComment === this.state.comment && newAnnotation === this.state.annotation) {
      return;
    }

    this.setState(
      {
        comment: newComment,
        annotation: newAnnotation,
        isExpanded: this.props.isInitiallyExpanded,
      },
      onFinish,
    );
  }

  private makeDraftComment(): CommentModel | undefined {
    const {panelId, conversation, user} = this.props;

    if (!conversation) {
      return undefined;
    }

    return buildDraftComment({
      urlBuilder: buildCommentUrlBuilder(conversation.url),
      authorId: user.id,
      conversationId: conversation.id,
      lastPanelId: panelId,
    });
  }

  /*
   * Dispatch methods.
   */

  private readonly saveComment = (options: PublicSaveCommentOptions) => {
    const {comment, annotation} = this.state;
    if (!comment) {
      return;
    }

    const {panelId} = this.props;

    const noTextChanges = options.newText === comment.text;
    const noAttachmentsChanges = !options.addedAttachments && !options.removedAttachmentCids;
    const noReferencedActivityChanges =
      options.newReferencedActivityId === undefined ||
      options.newReferencedActivityId === comment.referencedActivityId;

    const noAnnotationChanges =
      options.newAnnotation === undefined || isEqual(options.newAnnotation, annotation);

    // If nothing has changed, return now.
    if (
      noTextChanges &&
      noAttachmentsChanges &&
      noReferencedActivityChanges &&
      noAnnotationChanges &&
      !comment.error
    ) {
      return;
    }

    // Otherwise, save the comment.
    this.props.saveCommentAsync(comment, {
      ...options,
      panelId,
    });
  };

  private readonly onTextChange = (newText: string) => {
    this.saveComment({newText});
  };

  private readonly onExpandedToggle: (arg: EventEntryPointsEnum) => void = (entryPoint) => {
    this.setState(
      ({isExpanded}) => ({isExpanded: !isExpanded}),
      () => {
        this.notifyExpandedChange();

        trackInterfaceEvent(InterfaceEventsEnum.COMMENT_COMPOSER_EXPAND_ICON_CLICK, {
          conversation_id: this.props.conversation?.id,
          action: this.state.isExpanded ? 'expand' : 'collapse',
          entry_point: entryPoint,
        });
      },
    );
  };

  private readonly onBlurIfEmpty: () => void = () => {
    if (!this.state.isExpanded) {
      return;
    }

    this.setState({isExpanded: false}, () => {
      this.notifyExpandedChange();
    });
  };

  private notifyExpandedChange() {
    const {comment, isExpanded} = this.state;
    if (!comment) {
      return;
    }

    if (isExpanded) {
      this.props.expandCommentComposer(comment.uid);
    } else {
      this.props.collapseCommentComposer(comment.uid);
    }
  }

  /*
   * Attachments.
   */

  private readonly addAttachment = (attachment: UploadedAttachmentModel) => {
    // Save the comment with the newly added attachment.
    this.saveComment({addedAttachments: [attachment]});
  };

  private readonly removeAttachment = (cid: string) => {
    // Save the comment with the newly removed attachment.
    this.saveComment({removedAttachmentCids: [cid]});
  };

  private readonly sendComment = (options: SendCommentOptions) => {
    const {conversation, cellsContext} = this.props;
    const {comment} = this.state;
    if (!conversation || !comment) {
      throw new Error('No comment to send.');
    }

    this.updateStateComment(this.props.comment, this.props.annotation);
    if (this.props.annotation) {
      let messageSourceEventData;
      if (this.props.annotation.type === AnnotationTypesEnum.HIGHLIGHT_COMMENT) {
        messageSourceEventData = {message_source: AnnotationEventTrackingSourcesEnum.COMMENT};
      } else if (this.props.annotation.type === AnnotationTypesEnum.HIGHLIGHT_MESSAGE) {
        messageSourceEventData = {message_source: AnnotationEventTrackingSourcesEnum.EMAIL};
      } else {
        messageSourceEventData = {};
      }

      trackInterfaceEvent(InterfaceEventsEnum.P3_MESSAGE_TEXT_COMMENT, {
        namespace: conversation.namespace,
        conversation_id: conversation.id,
        has_additional_message: Boolean(this.props.comment?.text),
        ...messageSourceEventData,
      });
    }

    const giphyAttachments = (this.props.comment?.attachments ?? []).filter(
      (a) => isAttachmentModel(a) && a.giphyId !== undefined,
    );
    if (giphyAttachments.length > 0) {
      trackInterfaceEvent(InterfaceEventsEnum.COMMENT_COMPOSER_GIF_ICON_SEND, {
        conversation_id: conversation.id,
        num_items: giphyAttachments.length,
        has_additional_message: Boolean(this.props.comment?.text),
      });
    }

    const sendPromise = this.props.sendCommentAsync({
      cellsContext,
      conversationId: conversation.id,
      commentUid: comment.uid,
      ...options,
    });

    if (options.sendAction === SendActionsEnum.SEND_AND_ARCHIVE) {
      this.props.viewerConversationClosedRequested(this.props.panelId, conversation.id);
    }

    return sendPromise;
  };

  private readonly editLastComment = () => {
    const {conversation, panelId} = this.props;

    if (!conversation) {
      return;
    }

    this.props.editLastComment(panelId, conversation.id);
  };

  /*
   * Referenced Activity.
   */

  private readonly updateReferencedActivity = (newActivityId: number | null) => {
    const {comment} = this.state;
    if (!comment) {
      return;
    }

    this.saveComment({newReferencedActivityId: newActivityId, newAnnotation: null});
  };

  /*
   * Annotation.
   */

  private readonly updateAnnotation = (options: UpdateAnnotationOptions) => {
    const {comment} = this.state;
    if (!comment) {
      return;
    }

    this.saveComment({newAnnotation: options.annotation, newReferencedActivityId: options.activityId});
  };

  /*
   * Render.
   */

  render() {
    const {conversation, hasAutoShareDrafts, hasNewSynchronousP3} = this.props;
    const {comment} = this.state;
    if (!conversation || !comment) {
      return null;
    }

    const {attachments, referencedActivityId} = comment;
    const {annotation} = this.state;

    return wrapInDataEventContext(
      <CommentComposer
        panelId={this.props.panelId}
        company={this.props.company}
        conversation={conversation}
        isDisabled={this.props.isDisabled}
        isAIDisabled={this.props.isAIDisabled}
        shouldFocus={this.props.shouldFocus}
        user={this.props.user}
        mentionableAliases={this.props.mentionableAliases}
        referencedActivityId={referencedActivityId}
        annotation={annotation}
        comment={comment}
        attachments={attachments}
        editLastComment={this.editLastComment}
        endCommentEdit={() => {}}
        onTextChange={this.onTextChange}
        addAttachment={this.addAttachment}
        onRemoveAttachmentClick={this.removeAttachment}
        sendComment={this.sendComment}
        updateReferencedActivity={this.updateReferencedActivity}
        updateAnnotation={this.updateAnnotation}
        isExpanded={this.state.isExpanded}
        onExpandedToggle={this.onExpandedToggle}
        onBlurIfEmpty={this.onBlurIfEmpty}
        hasContentHeight
        // We hide the comment composer info bubbles if there are popup panels that could obscure the bubble
        shouldHideCommentComposerInfoBubble={this.props.hasPopupPanel}
        emojiValidationMap={this.props.emojiValidationMap}
        hasAutoShareDrafts={hasAutoShareDrafts}
        setComposerHeight={this.props.setComposerHeight}
        cellsContext={this.props.cellsContext}
        hasNewSynchronousP3={hasNewSynchronousP3}
      />,
      'comment_composer',
      {location: composerLocation(this.props.hasPopupPanel)},
    );
  }
}

/*
 * Redux.
 */

const mapStateToProps = makeMapStateToProps<CommentComposerContainerPublicProps>()(() => {
  const getConversation = makeGetConversation();
  const getIsDisabled = makeGetIsDisabled(getConversation);
  const getComment = makeGetComment(getConversation);
  const getCommentUid = makeGetCommentUid(getComment);
  const getAnnotation = makeGetAnnotationForComment(getComment);
  const getConversationMentionableAliases = makeGetConversationMentionableAliases(getConversation);
  const getIsAIDisabled = makeGetIsAIDisabled();

  return {
    company: getCompany,
    user: getUser,
    conversation: getConversation,
    comment: getComment,
    annotation: getAnnotation,
    mentionableAliases: getConversationMentionableAliases,
    isDisabled: getIsDisabled,
    isAIDisabled: getIsAIDisabled,
    isInitiallyExpanded: makeGetIsCommentComposerExpanded(getCommentUid),

    hasPopupPanel: makeGetHasPopupPanel(),
    emojiValidationMap: getEmojiValidationMap,
    hasAutoShareDrafts: getHasAutoShareDrafts,
    hasNewSynchronousP3: getHasNewSynchronousP3,
  };
});

const mapDispatchToProps = makeMapDispatchToProps()(() => ({
  editLastComment: editLastCommentAction,
  saveCommentAsync: saveCommentAsyncAction,
  sendCommentAsync: sendCommentAsyncAction,
  viewerConversationClosedRequested: viewerConversationCloseRequestedAction,
  expandCommentComposer: expandCommentComposerAction,
  collapseCommentComposer: collapseCommentComposerAction,
}));

export const CommentComposerContainer = connect(mapStateToProps, mapDispatchToProps, (s, d, o) => ({
  ...s,
  ...o,
  ...d,
}))(CommentComposerContainerComponent);
